<template>
  <div class="cover-template-editor">
    <!-- Preview area - now showing the actual canvas -->
    <div class="template-preview-container">
      <div
        class="template-preview"
        @contextmenu.prevent
      >
        <canvas
          ref="previewCanvas"
          width="500"
          height="500"
          class="preview-canvas"
        />
      </div>
    </div>

    <!-- Controls -->
    <div class="template-controls">
      <!-- Template selector -->
      <div class="control-section">
        <label class="control-label">{{ $t('releases.template-style') }}</label>
        <div class="template-selector">
          <button
            v-for="template in templates"
            :key="template.id"
            class="template-button"
            :class="{
              'active': isTemplateActive(template.id),
              'premium': template.premium
            }"
            @click="handleTemplateClick(template)"
          >
            <span class="template-name">{{ template.name }}</span>
            <i
              v-if="template.premium && !isPremium"
              class="fas fa-lock premium-icon"
            />
          </button>
        </div>
      </div>

      <!-- Color controls -->
      <div class="control-section">
        <!-- Color palette selector -->
        <div>
          <label class="control-label">{{ $t('releases.color-palettes') || 'Color Palettes' }}</label>
          <div class="palette-selector">
            <div
              v-for="(palette, index) in colorPalettes[selectedTemplate]"
              :key="index"
              class="palette-option"
              :class="{ 'active': selectedPalette === index }"
              @click="applyPalette(index)"
            >
              <div class="palette-preview">
                <div
                  class="palette-color primary-color"
                  :style="{ backgroundColor: palette.primary }"
                />
                <div
                  v-if="hasSecondaryColor"
                  class="palette-color accent-color"
                  :style="{ backgroundColor: palette.accent }"
                />
                <div
                  v-if="hasBackgroundColor"
                  class="palette-color background-color"
                  :style="{ backgroundColor: palette.background }"
                />
              </div>
              <span class="palette-name">{{ palette.name }}</span>
            </div>
          </div>
        </div>

        <!-- Manual color controls toggle -->
        <div class="custom-colors-toggle">
          <div
            class="control-subtitle-wrapper"
            @click="showCustomColors = !showCustomColors"
          >
            <h4 class="control-subtitle">
              {{ $t('releases.custom-colors') || 'Custom Colors' }}
            </h4>
            <span class="toggle-icon">
              <i :class="showCustomColors ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" />
            </span>
          </div>
        </div>

        <!-- Manual color controls -->
        <div
          v-show="showCustomColors"
          class="manual-color-controls"
        >
          <label class="control-label mt-3">{{ $t('releases.primary-color') }}</label>
          <div class="color-picker-row">
            <input
              v-model="primaryColor"
              type="color"
              class="color-picker"
              @change="updateTemplate"
            >
            <span class="color-value">{{ primaryColor }}</span>
          </div>

          <div v-if="hasSecondaryColor">
            <label class="control-label mt-3">{{ $t('releases.secondary-color') }}</label>
            <div class="color-picker-row">
              <input
                v-model="accentColor"
                type="color"
                class="color-picker"
                @change="updateTemplate"
              >
              <span class="color-value">{{ accentColor }}</span>
            </div>
          </div>

          <div v-if="hasBackgroundColor">
            <label class="control-label mt-3">{{ $t('releases.background-color') }}</label>
            <div class="color-picker-row">
              <input
                v-model="backgroundColor"
                type="color"
                class="color-picker"
                @change="updateTemplate"
              >
              <span class="color-value">{{ backgroundColor }}</span>
            </div>
          </div>

          <div>
            <label class="control-label mt-3">{{ $t('releases.text-color') }}</label>
            <div class="color-picker-row">
              <input
                v-model="textColor"
                type="color"
                class="color-picker"
                @change="updateTemplate"
              >
              <span class="color-value">{{ textColor }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Text options -->
      <div class="control-section">
        <div class="toggle-control">
          <ToggleSwitch
            v-model="showTitle"
            :label="$t('releases.display-title-on-artwork')"
            @update:model-value="updateTemplate"
          />
        </div>

        <div
          v-if="showTitle"
          class="mt-3"
        >
          <label class="control-label">{{ $t('releases.text-color') }}</label>
          <div class="color-picker-row">
            <input
              v-model="textColor"
              type="color"
              class="color-picker"
              @change="updateTemplate"
            >
            <span class="color-value">{{ textColor }}</span>
          </div>
        </div>

        <!-- Artistic template specific controls -->
        <div
          v-if="selectedTemplate === 'artistic' && showTitle"
          class="artistic-controls mt-4"
        >
          <label class="control-label">{{ $t('releases.text-position') || 'Text Position' }}</label>
          <div class="position-selector mb-3">
            <button
              v-for="position in [
                { value: 'bottom-left', label: $t('releases.bottom-left') || 'Bottom Left' },
                { value: 'center', label: $t('releases.center') || 'Center' },
                { value: 'bottom-right', label: $t('releases.bottom-right') || 'Bottom Right' }
              ]"
              :key="position.value"
              class="position-button"
              :class="{ 'active': textPosition === position.value }"
              @click="textPosition = position.value; updateTemplate()"
            >
              {{ position.label }}
            </button>
          </div>

          <label class="control-label">{{ $t('releases.pattern-type') || 'Pattern Type' }}</label>
          <div class="position-selector mb-3">
            <button
              v-for="pattern in [
                { value: 'geometric', label: $t('releases.pattern-geometric') || 'Geometric' },
                { value: 'waves', label: $t('releases.pattern-waves') || 'Waves' }
              ]"
              :key="pattern.value"
              class="position-button"
              :class="{ 'active': patternType === pattern.value }"
              @click="patternType = pattern.value; updateTemplate()"
            >
              {{ pattern.label }}
            </button>
          </div>

          <div class="toggle-control">
            <ToggleSwitch
              v-model="enableTextShadow"
              :label="$t('releases.enable-text-shadow') || 'Enable Text Shadow'"
              @update:model-value="updateTemplate"
            />
          </div>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="control-actions">
        <BaseButton
          variant="primary"
          size="medium"
          :is-gradient="true"
          :is-loading="isGenerating"
          :is-full-width="true"
          :is-disabled="(isCurrentTemplatePremium && !isPremium) || isGenerating"
          :icon="isCurrentTemplatePremium && !isPremium ? 'fas fa-lock' : ''"
          @click="generateArtwork"
        >
          {{ isGenerating ? $t('general.loading') : $t('releases.generate-artwork') }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import ToggleSwitch from '@/components/ui/ToggleSwitch.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import darkLogo from '@/assets/images/indiefy-icon-black.png';
import lightLogo from '@/assets/images/indiefy-icon-white.png';
import { useRouter } from 'vue-router';

const { t } = useI18n();

// Router
const router = useRouter();

// Helper function to determine if a color is dark
const isDarkColor = (color) => {
  // Convert hex to RGB
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
};

// Props
const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  artist: {
    type: String,
    default: '',
  },
  initialTemplate: {
    type: String,
    default: 'minimal',
  },
  isPremium: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits(['update:artwork']);

// Template options
const templates = computed(() => [
  { id: 'text', name: t('releases.template-text'), premium: false },
  { id: 'minimal', name: t('releases.template-minimal'), premium: true },
  { id: 'gradient', name: t('releases.template-gradient'), premium: true },
  { id: 'vintage', name: t('releases.template-vintage'), premium: true },
  { id: 'abstract', name: t('releases.template-abstract'), premium: true },
  { id: 'artistic', name: t('releases.template-artistic'), premium: true },
]);

// Refs for canvas generation
const previewCanvas = ref(null);
const previewCtx = ref(null);

// Template state
const selectedTemplate = ref(props.initialTemplate);
const primaryColor = ref('#3e8ed0');
const accentColor = ref('#6c5ce7');
const backgroundColor = ref('#ffffff');
const textColor = ref('#000000');
const artistColor = ref('#2b6cb0'); // Artist text color for minimal template
const showTitle = ref(true);

// Artistic template customization options
const textPosition = ref('bottom-left'); // 'center', 'bottom-left', 'bottom-right'
const enableTextShadow = ref(true);
const patternType = ref('geometric'); // 'geometric', 'waves'

// Loading state
const isGenerating = ref(false);

// Color palettes for each template type
const colorPalettes = {
  minimal: [
    { name: 'Ocean Blue', primary: '#3e8ed0', background: '#f0f8ff', text: '#1a3c5e', artistColor: '#2b6cb0' },
    { name: 'Forest Green', primary: '#38a169', background: '#f0fff4', text: '#1e4a33', artistColor: '#48bb78' },
    { name: 'Ruby Red', primary: '#e53e3e', background: '#fff5f5', text: '#742a2a', artistColor: '#c53030' },
    { name: 'Royal Purple', primary: '#805ad5', background: '#f8f4ff', text: '#44337a', artistColor: '#9f7aea' },
    { name: 'Sunset Orange', primary: '#ed8936', background: '#fffaf0', text: '#7b341e', artistColor: '#dd6b20' },
    { name: 'Night Sky', primary: '#4299e1', background: '#1a202c', text: '#ffffff', artistColor: '#63b3ed' },
    { name: 'Desert Sand', primary: '#dd6b20', background: '#fffbeb', text: '#652b19', artistColor: '#ed8936' },
    { name: 'Mint Green', primary: '#38b2ac', background: '#e6fffa', text: '#234e52', artistColor: '#4fd1c5' },
    { name: 'Berry Pink', primary: '#d53f8c', background: '#fff5f7', text: '#702459', artistColor: '#ed64a6' },
    { name: 'Deep Teal', primary: '#319795', background: '#1a202c', text: '#e6fffa', artistColor: '#4fd1c5' },
  ],
  gradient: [
    { name: 'Sunset', primary: '#f6ad55', accent: '#e53e3e', text: '#ffffff', artistColor: '#fbd38d' },
    { name: 'Ocean', primary: '#2b6cb0', accent: '#38b2ac', text: '#ffffff', artistColor: '#e6fffa' },
    { name: 'Forest', primary: '#38a169', accent: '#9ae6b4', text: '#ffffff', artistColor: '#f0fff4' },
    { name: 'Berry', primary: '#9f7aea', accent: '#e53e3e', text: '#ffffff', artistColor: '#d6b4f7' },
    { name: 'Midnight', primary: '#2d3748', accent: '#4a5568', text: '#ffffff', artistColor: '#cbd5e0' },
    { name: 'Aurora', primary: '#4299e1', accent: '#9f7aea', text: '#ffffff', artistColor: '#90cdf4' },
    { name: 'Fire', primary: '#ed8936', accent: '#f56565', text: '#ffffff', artistColor: '#fff5f5' },
    { name: 'Cotton Candy', primary: '#ed64a6', accent: '#667eea', text: '#ffffff', artistColor: '#f093c0' },
    { name: 'Deep Sea', primary: '#2c5282', accent: '#285e61', text: '#ffffff', artistColor: '#4299e1' },
    { name: 'Autumn', primary: '#dd6b20', accent: '#975a16', text: '#ffffff', artistColor: '#f6ad55' },
  ],
  vintage: [
    { name: 'Sepia', primary: '#975a16', background: '#f5f5dc', text: '#321414', artistColor: '#b8651a' },
    { name: 'Coffee', primary: '#8b4513', background: '#f0e6d2', text: '#3c2f26', artistColor: '#a0522d' },
    { name: 'Old Paper', primary: '#a0522d', background: '#faebd7', text: '#4b2613', artistColor: '#cd853f' },
    { name: 'Faded Photo', primary: '#6b705c', background: '#ede0d4', text: '#3f4238', artistColor: '#8b9467' },
    { name: 'Antique', primary: '#7f5539', background: '#efe6dd', text: '#3d271b', artistColor: '#a0633f' },
    { name: 'Retro', primary: '#744253', background: '#f2e9e4', text: '#382030', artistColor: '#8b4f63' },
    { name: 'Tobacco', primary: '#9c6644', background: '#f1efdc', text: '#4d331f', artistColor: '#bf7f4f' },
    { name: 'Parchment', primary: '#8e7f7f', background: '#eadfcf', text: '#463f3f', artistColor: '#a99b9b' },
    { name: 'Classic', primary: '#985f6f', background: '#f4f1de', text: '#4c2e37', artistColor: '#b8737f' },
    { name: 'Heritage', primary: '#4a5859', background: '#e0e2db', text: '#31383a', artistColor: '#6b7a7c' },
  ],
  abstract: [
    { name: 'Vivid', primary: '#ff7979', accent: '#6c5ce7', background: '#f9f9f9', text: '#2f3542', artistColor: '#a855f7' },
    { name: 'Bold', primary: '#38b2ac', accent: '#fc8181', background: '#2d3748', text: '#ffffff', artistColor: '#f687b3' },
    { name: 'Playful', primary: '#fc8181', accent: '#4fd1c5', background: '#f7fafc', text: '#2d3748', artistColor: '#81e6d9' },
    { name: 'Contrast', primary: '#f6ad55', accent: '#9f7aea', background: '#1a202c', text: '#f7fafc', artistColor: '#c4b5fd' },
    { name: 'Pastel', primary: '#b794f4', accent: '#9decf9', background: '#f5f6fa', text: '#4a5568', artistColor: '#bfdbfe' },
    { name: 'Pop Art', primary: '#fbd38d', accent: '#63b3ed', background: '#feb2b2', text: '#2a4365', artistColor: '#93c5fd' },
    { name: 'Neon', primary: '#7fffd4', accent: '#ff6b6b', background: '#191919', text: '#ffffff', artistColor: '#ff8a80' },
    { name: 'Minimal', primary: '#e2e8f0', accent: '#a0aec0', background: '#2d3748', text: '#f7fafc', artistColor: '#cbd5e0' },
    { name: 'Creative', primary: '#4c51bf', accent: '#48bb78', background: '#f3f4f6', text: '#1a202c', artistColor: '#68d391' },
    { name: 'Energy', primary: '#ffbe0b', accent: '#fb5607', background: '#f2f7f7', text: '#3b434f', artistColor: '#ff8c42' },
  ],
  text: [
    { name: 'Classic Black', primary: '#ffffff', background: '#000000', text: '#cccccc' },
    { name: 'Clean White', primary: '#2d3748', background: '#ffffff', text: '#4a5568' },
    { name: 'Vibrant Blue', primary: '#4299e1', background: '#1a202c', text: '#a0aec0' },
    { name: 'Red Focus', primary: '#f56565', background: '#1a202c', text: '#cbd5e0' },
    { name: 'Night Mode', primary: '#90cdf4', background: '#1a202c', text: '#e2e8f0' },
    { name: 'Gold', primary: '#f6ad55', background: '#1a202c', text: '#ecc94b' },
    { name: 'Forest', primary: '#68d391', background: '#1a202c', text: '#9ae6b4' },
    { name: 'Purple Haze', primary: '#d6bcfa', background: '#2a4365', text: '#e9d8fd' },
    { name: 'Steel', primary: '#a0aec0', background: '#2d3748', text: '#e2e8f0' },
    { name: 'Crimson', primary: '#fed7d7', background: '#742a2a', text: '#fff5f5' },
  ],
  artistic: [
    // Professional gradient palettes with unique backgrounds
    { name: 'Sunset', primary: '#f6ad55', accent: '#e53e3e', background: '#2a1810', text: '#ffffff', artistColor: '#fbd38d' },
    { name: 'Ocean', primary: '#2b6cb0', accent: '#38b2ac', background: '#0f2a3a', text: '#ffffff', artistColor: '#63b3ed' },
    { name: 'Forest', primary: '#38a169', accent: '#9ae6b4', background: '#1a2e1a', text: '#ffffff', artistColor: '#68d391' },
    { name: 'Berry', primary: '#9f7aea', accent: '#e53e3e', background: '#2d1b3d', text: '#ffffff', artistColor: '#d6b4f7' },
    { name: 'Aurora', primary: '#4299e1', accent: '#9f7aea', background: '#1a2b3a', text: '#ffffff', artistColor: '#90cdf4' },
    { name: 'Fire', primary: '#ed8936', accent: '#f56565', background: '#3a1f0f', text: '#ffffff', artistColor: '#fbb36b' },
    { name: 'Cotton Candy', primary: '#ed64a6', accent: '#667eea', background: '#3a1a2a', text: '#ffffff', artistColor: '#f093c0' },
    { name: 'Deep Sea', primary: '#2c5282', accent: '#285e61', background: '#0a1a2a', text: '#ffffff', artistColor: '#4299e1' },
    { name: 'Autumn', primary: '#dd6b20', accent: '#975a16', background: '#2a1a0f', text: '#ffffff', artistColor: '#f6ad55' },
    // Additional professional palettes
    { name: 'Royal Gold', primary: '#d69e2e', accent: '#f6ad55', background: '#2a2010', text: '#ffffff', artistColor: '#f6e05e' },
    { name: 'Rose Gold', primary: '#ed64a6', accent: '#f7931e', background: '#3a1a15', text: '#ffffff', artistColor: '#f687b3' },
    { name: 'Charcoal', primary: '#4a5568', accent: '#718096', background: '#1a1d20', text: '#ffffff', artistColor: '#9ca3af' },
    { name: 'Emerald', primary: '#38a169', accent: '#48bb78', background: '#102820', text: '#ffffff', artistColor: '#6ee7b7' },
    { name: 'Electric Blue', primary: '#3182ce', accent: '#63b3ed', background: '#0f1f3a', text: '#ffffff', artistColor: '#7dd3fc' },
  ],
};

// Selected palette index for current template
const selectedPalette = ref(-1);

// Toggle state for custom colors section
const showCustomColors = ref(false);

// Computed properties for template-specific controls
const hasSecondaryColor = computed(() => {
  return ['gradient', 'abstract', 'artistic'].includes(selectedTemplate.value);
});

const hasBackgroundColor = computed(() => {
  return ['minimal', 'text', 'vintage', 'abstract', 'artistic'].includes(selectedTemplate.value);
});

const hasArtistColor = computed(() => {
  return ['minimal', 'gradient', 'vintage', 'abstract', 'artistic'].includes(selectedTemplate.value);
});

// Computed property to check if current template is premium
const isCurrentTemplatePremium = computed(() => {
  const currentTemplate = templates.value.find(t => t.id === selectedTemplate.value);
  return currentTemplate?.premium || false;
});

// Methods
const handleTemplateClick = (template) => {
  setTemplate(template.id);
};

const isTemplateActive = (templateId) => {
  return selectedTemplate.value === templateId;
};

const setTemplate = (templateId) => {
  selectedTemplate.value = templateId;
  selectedPalette.value = -1; // Reset selected palette when changing templates

  // Set appropriate default colors based on template
  if (templateId === 'minimal') {
    // Apply the first palette by default
    if (colorPalettes.minimal && colorPalettes.minimal.length > 0) {
      applyPalette(0);
    } else {
      backgroundColor.value = '#e5eeff';
      textColor.value = '#000000';
      primaryColor.value = '#3e8ed0';
    }
  } else if (templateId === 'gradient') {
    // Apply the first palette by default
    if (colorPalettes.gradient && colorPalettes.gradient.length > 0) {
      applyPalette(0);
    } else {
      primaryColor.value = '#3e8ed0';
      accentColor.value = '#6c5ce7';
      textColor.value = '#ffffff';
    }
  } else if (templateId === 'vintage') {
    // Apply the first palette by default
    if (colorPalettes.vintage && colorPalettes.vintage.length > 0) {
      applyPalette(0);
    } else {
      backgroundColor.value = '#f5f5dc'; // Default vintage paper background
      primaryColor.value = '#a0522d';
      textColor.value = '#321414';
    }
  } else if (templateId === 'abstract') {
    // Apply the first palette by default
    if (colorPalettes.abstract && colorPalettes.abstract.length > 0) {
      applyPalette(0);
    } else {
      primaryColor.value = '#ff7979';
      accentColor.value = '#6c5ce7';
      textColor.value = '#2f3542';
      backgroundColor.value = '#f9f9f9';
    }
  } else if (templateId === 'text') {
    // Apply the first palette by default
    if (colorPalettes.text && colorPalettes.text.length > 0) {
      applyPalette(0);
    } else {
      backgroundColor.value = '#000000';
      primaryColor.value = '#ffffff';
      textColor.value = '#cccccc';
    }
  } else if (templateId === 'artistic') {
    // Apply the first palette by default
    if (colorPalettes.artistic && colorPalettes.artistic.length > 0) {
      applyPalette(0);
    } else {
      backgroundColor.value = '#0a0a0a';
      primaryColor.value = '#ff0080';
      accentColor.value = '#00ff80';
      textColor.value = '#ffffff';
    }
  }

  renderPreview();
};

const updateTemplate = () => {
  // Render the preview when template settings change
  renderPreview();
};

// Apply a color palette to the current template
const applyPalette = (index) => {
  // Get template-specific palettes
  const palettes = colorPalettes[selectedTemplate.value];
  if (!palettes || index >= palettes.length) return;

  // Get the selected palette
  const palette = palettes[index];

  // Apply palette colors
  primaryColor.value = palette.primary;

  if (palette.accent) {
    accentColor.value = palette.accent;
  }

  if (palette.background) {
    backgroundColor.value = palette.background;
  }

  if (palette.text) {
    textColor.value = palette.text;
  }

  if (palette.artistColor) {
    artistColor.value = palette.artistColor;
  }

  selectedPalette.value = index;

  // Update the preview
  renderPreview();
};

// New method to render the canvas preview
const renderPreview = async () => {
  if (!previewCanvas.value) return;

  // Get context
  const ctx = previewCanvas.value.getContext('2d');
  previewCtx.value = ctx;

  // Clear canvas
  ctx.clearRect(0, 0, previewCanvas.value.width, previewCanvas.value.height);

  // Render the selected template
  await renderTemplate(ctx, previewCanvas.value.width, previewCanvas.value.height);
};

// Helper function for intelligent text sizing
const calculateOptimalTextSize = (ctx, text, maxWidth, baseSize, minSize, scale = 1, isBold = true) => {
  let fontSize = baseSize;
  const fontWeight = isBold ? 'bold' : 'normal';
  ctx.font = `${fontWeight} ${fontSize}px Arial`;
  let textWidth = ctx.measureText(text).width;

  // More intelligent scaling - larger reduction for longer text
  while (textWidth > maxWidth && fontSize > minSize) {
    const reductionFactor = textWidth > maxWidth * 1.5 ? scale * 3 : scale * 1.5;
    fontSize -= reductionFactor;
    fontSize = Math.max(fontSize, minSize);
    ctx.font = `${fontWeight} ${fontSize}px Arial`;
    textWidth = ctx.measureText(text).width;
  }

  return fontSize;
};

// Helper function to wrap text into multiple lines
const wrapText = (ctx, text, maxWidth) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  for (let i = 0; i < words.length; i++) {
    const testLine = currentLine + (currentLine ? ' ' : '') + words[i];
    const testWidth = ctx.measureText(testLine).width;

    if (testWidth > maxWidth && currentLine !== '') {
      lines.push(currentLine);
      currentLine = words[i];
    } else {
      currentLine = testLine;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
};

// Helper function to render multi-line text with proper spacing
const renderMultiLineText = (ctx, text, x, y, maxWidth, fontSize, isBold = true, textAlign = 'left') => {
  const fontWeight = isBold ? 'bold' : 'normal';
  ctx.font = `${fontWeight} ${fontSize}px Arial`;
  ctx.textAlign = textAlign;

  const lines = wrapText(ctx, text, maxWidth, isBold);
  const lineHeight = fontSize * 1.2; // 20% line spacing

  // Calculate starting Y position for centered multi-line text
  const totalHeight = (lines.length - 1) * lineHeight;
  let startY = y;

  if (lines.length > 1) {
    startY = y - (totalHeight / 2);
  }

  lines.forEach((line, index) => {
    ctx.fillText(line, x, startY + (index * lineHeight));
  });

  return {
    lines,
    totalHeight,
    lineHeight,
  };
};

// Method to render a template to any canvas context - Updated spacing fixes
const renderTemplate = async (ctx, width, height, scale = 1) => {
  // Calculate padding based on canvas size
  const padding = Math.max(40 * scale, width * 0.06);

  // Calculate text sizes with scaling - optimized for thumbnail visibility
  const titleFontSize = Math.max(55 * scale, width * 0.13);
  const artistFontSize = Math.max(45 * scale, width * 0.10);

  // Calculate the bottom padding to position text safely away from the edge
  const bottomPadding = Math.max(50 * scale, height * 0.1);

  // Get text to display with fallbacks
  const titleText = props.title || 'Release Title';
  const artistText = props.artist || 'Artist Name';

  // Reset any previous text settings
  ctx.textAlign = 'left';
  ctx.textBaseline = 'bottom';
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;

  // Fill the background
  ctx.fillStyle = backgroundColor.value;
  ctx.fillRect(0, 0, width, height);

  // Render template-specific elements
  if (selectedTemplate.value === 'minimal') {
    // Draw minimal bar at bottom
    const barHeight = Math.max(10 * scale, height * 0.03);
    ctx.fillStyle = primaryColor.value;
    ctx.fillRect(0, height - barHeight, width, barHeight);

    // Add text if needed
    if (showTitle.value) {
      const maxTextWidth = width - (padding * 2);
      const minTitleSize = Math.max(28 * scale, width * 0.06);
      const minArtistSize = Math.max(24 * scale, width * 0.05);

      // Calculate optimal sizes using helper function, ensuring title is always bigger
      let actualTitleSize = calculateOptimalTextSize(ctx, titleText, maxTextWidth, titleFontSize, minTitleSize, scale, true);
      const actualArtistSize = calculateOptimalTextSize(ctx, artistText, maxTextWidth, artistFontSize, minArtistSize, scale, true);

      // Ensure title is always at least 1.15x bigger than artist
      if (actualTitleSize <= actualArtistSize) {
        actualTitleSize = Math.max(actualArtistSize * 1.15, minTitleSize);
      }

      // First, calculate how much space the artist text will need
      ctx.font = `bold ${actualArtistSize}px Arial`;
      const artistLines = wrapText(ctx, artistText, maxTextWidth);
      const artistTotalHeight = artistLines.length * actualArtistSize * 1.2;

      // Position title above the artist text with spacing based on text length for minimal
      const titleLines = wrapText(ctx, titleText, maxTextWidth);

      // Calculate intelligent spacing based on text length with smooth scaling
      const combinedTextLength = titleText.length + artistText.length;

      // Create a smooth scaling factor based on text length (8-50 characters range)
      const minLength = 8;
      const maxLength = 50;
      const clampedLength = Math.max(minLength, Math.min(maxLength, combinedTextLength));
      const lengthRatio = (clampedLength - minLength) / (maxLength - minLength);

      // Scale spacing from 0.10x to 1.4x based on text length (very aggressive for short text)
      const spacingMultiplier = 0.10 + (lengthRatio * 1.30);
      const spacingBetweenTitleAndArtist = Math.max(actualTitleSize * spacingMultiplier, actualArtistSize * (spacingMultiplier * 0.8));

      // Render title with primary text color
      ctx.fillStyle = textColor.value;
      const titleY = height - bottomPadding - artistTotalHeight - spacingBetweenTitleAndArtist;
      renderMultiLineText(
        ctx, titleText, padding, titleY,
        maxTextWidth, actualTitleSize, true, 'left',
      );

      // Render artist with dedicated artist color for visual differentiation
      ctx.fillStyle = artistColor.value;
      const artistY = height - bottomPadding - (artistLines.length - 1) * (actualArtistSize * 1.2);
      renderMultiLineText(
        ctx, artistText, padding, artistY,
        maxTextWidth, actualArtistSize, true, 'left',
      );
    }
  }
  else if (selectedTemplate.value === 'gradient') {
    // Create opaque gradient (no transparency to avoid background interference)
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, primaryColor.value);
    gradient.addColorStop(1, accentColor.value);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Add text if needed
    if (showTitle.value) {
      const maxTextWidth = width - (padding * 2);
      const minTitleSize = Math.max(28 * scale, width * 0.06);
      const minArtistSize = Math.max(24 * scale, width * 0.05);

      // Calculate optimal sizes using helper function, ensuring title is always bigger
      let actualTitleSize = calculateOptimalTextSize(ctx, titleText, maxTextWidth, titleFontSize, minTitleSize, scale, true);
      const actualArtistSize = calculateOptimalTextSize(ctx, artistText, maxTextWidth, artistFontSize, minArtistSize, scale, true);

      // Ensure title is always at least 1.15x bigger than artist
      if (actualTitleSize <= actualArtistSize) {
        actualTitleSize = Math.max(actualArtistSize * 1.15, minTitleSize);
      }

      // First, calculate how much space the artist text will need
      ctx.font = `bold ${actualArtistSize}px Arial`;
      const artistLines = wrapText(ctx, artistText, maxTextWidth);
      const artistTotalHeight = artistLines.length * actualArtistSize * 1.2;

      // Position title above the artist text with spacing based on text length for gradient
      const titleLines = wrapText(ctx, titleText, maxTextWidth);

      // Calculate intelligent spacing based on text length with smooth scaling
      const combinedTextLength = titleText.length + artistText.length;

      // Create a smooth scaling factor based on text length (8-50 characters range)
      const minLength = 8;
      const maxLength = 50;
      const clampedLength = Math.max(minLength, Math.min(maxLength, combinedTextLength));
      const lengthRatio = (clampedLength - minLength) / (maxLength - minLength);

      // Scale spacing from 0.10x to 1.4x based on text length (very aggressive for short text)
      const spacingMultiplier = 0.10 + (lengthRatio * 1.30);
      const spacingBetweenTitleAndArtist = Math.max(actualTitleSize * spacingMultiplier, actualArtistSize * (spacingMultiplier * 0.8));

      // Render title with primary text color
      ctx.fillStyle = textColor.value;
      const titleY = height - bottomPadding - artistTotalHeight - spacingBetweenTitleAndArtist;
      renderMultiLineText(
        ctx, titleText, padding, titleY,
        maxTextWidth, actualTitleSize, true, 'left',
      );

      // Render artist with dedicated artist color for visual differentiation
      ctx.fillStyle = artistColor.value;
      const artistY = height - bottomPadding - (artistLines.length - 1) * (actualArtistSize * 1.2);
      renderMultiLineText(
        ctx, artistText, padding, artistY,
        maxTextWidth, actualArtistSize, true, 'left',
      );
    }
  }
  else if (selectedTemplate.value === 'vintage') {
    // Draw thicker frame border for more prominence
    const borderWidth = Math.max(8 * scale, width * 0.02);
    const frameInset = Math.max(20 * scale, width * 0.04);
    ctx.strokeStyle = primaryColor.value + '99';
    ctx.lineWidth = borderWidth;
    ctx.strokeRect(frameInset, frameInset, width - (frameInset * 2), height - (frameInset * 2));

    // Add slight texture
    const textureCount = Math.min(1000, width * height / 500);
    for (let i = 0; i < textureCount; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.02)';
      ctx.fillRect(x, y, scale, scale);
    }

    // Add text if needed with improved sizing and positioning
    if (showTitle.value) {
      // Enhanced padding to account for thicker border
      const vintagePadding = Math.max(70 * scale, width * 0.09);
      const maxTextWidth = width - (vintagePadding * 2);
      const minTitleSize = Math.max(28 * scale, width * 0.06);
      const minArtistSize = Math.max(24 * scale, width * 0.05);

      // Calculate optimal sizes using helper function, ensuring title is always bigger
      let actualTitleSize = calculateOptimalTextSize(ctx, titleText, maxTextWidth, titleFontSize, minTitleSize, scale, true);
      const actualArtistSize = calculateOptimalTextSize(ctx, artistText, maxTextWidth, artistFontSize, minArtistSize, scale, true);

      // Ensure title is always at least 1.15x bigger than artist
      if (actualTitleSize <= actualArtistSize) {
        actualTitleSize = Math.max(actualArtistSize * 1.15, minTitleSize);
      }

      // First, calculate how much space the artist text will need
      ctx.font = `bold ${actualArtistSize}px Arial`;
      const artistLines = wrapText(ctx, artistText, maxTextWidth);
      const artistTotalHeight = artistLines.length * actualArtistSize * 1.2;

      // Position title above the artist text with spacing based on text length for vintage
      const titleLines = wrapText(ctx, titleText, maxTextWidth);

      // Calculate intelligent spacing based on text length with smooth scaling
      const combinedTextLength = titleText.length + artistText.length;

      // Create a smooth scaling factor based on text length (8-50 characters range)
      const minLength = 8;
      const maxLength = 50;
      const clampedLength = Math.max(minLength, Math.min(maxLength, combinedTextLength));
      const lengthRatio = (clampedLength - minLength) / (maxLength - minLength);

      // Scale spacing from 0.10x to 1.6x based on text length (vintage has more generous spacing)
      const spacingMultiplier = 0.10 + (lengthRatio * 1.50);
      const spacingBetweenTitleAndArtist = Math.max(actualTitleSize * spacingMultiplier, actualArtistSize * (spacingMultiplier * 0.8));

      // Render title with primary text color
      ctx.fillStyle = textColor.value;
      const titleY = height - vintagePadding - artistTotalHeight - spacingBetweenTitleAndArtist;
      renderMultiLineText(
        ctx, titleText, vintagePadding, titleY,
        maxTextWidth, actualTitleSize, true, 'left',
      );

      // Render artist with dedicated artist color for visual differentiation
      ctx.fillStyle = artistColor.value;
      const artistY = height - vintagePadding - (artistLines.length - 1) * (actualArtistSize * 1.2);
      renderMultiLineText(
        ctx, artistText, vintagePadding, artistY,
        maxTextWidth, actualArtistSize, true, 'left',
      );
    }
  }
  else if (selectedTemplate.value === 'abstract') {
    // Fill with background color first
    ctx.fillStyle = backgroundColor.value;
    ctx.fillRect(0, 0, width, height);

    // Draw abstract shapes with relative sizes
    const shape1Radius = width * 0.3;
    const shape2Radius = width * 0.25;
    const shape3Radius = width * 0.2;

    // Shape 1
    ctx.beginPath();
    ctx.arc(-width * 0.05, -height * 0.05, shape1Radius, 0, 2 * Math.PI);
    ctx.fillStyle = primaryColor.value + 'B3'; // 70% opacity
    ctx.fill();

    // Shape 2
    ctx.beginPath();
    ctx.arc(width * 0.2, height + (height * 0.1), shape2Radius, 0, 2 * Math.PI);
    ctx.fillStyle = accentColor.value + '99'; // 60% opacity
    ctx.fill();

    // Shape 3
    ctx.beginPath();
    ctx.arc(width + (width * 0.05), height * 0.6, shape3Radius, 0, 2 * Math.PI);
    ctx.fillStyle = primaryColor.value + '80'; // 50% opacity
    ctx.fill();

    // Add text if needed with intelligent spacing
    if (showTitle.value) {
      const maxTextWidth = width - (padding * 2);
      const minTitleSize = Math.max(28 * scale, width * 0.06);
      const minArtistSize = Math.max(24 * scale, width * 0.05);

      // Calculate optimal sizes using helper function, ensuring title is always bigger
      let actualTitleSize = calculateOptimalTextSize(ctx, titleText, maxTextWidth, titleFontSize, minTitleSize, scale, true);
      const actualArtistSize = calculateOptimalTextSize(ctx, artistText, maxTextWidth, artistFontSize, minArtistSize, scale, true);

      // Ensure title is always at least 1.15x bigger than artist
      if (actualTitleSize <= actualArtistSize) {
        actualTitleSize = Math.max(actualArtistSize * 1.15, minTitleSize);
      }

      // First, calculate how much space the artist text will need
      ctx.font = `bold ${actualArtistSize}px Arial`;
      const artistLines = wrapText(ctx, artistText, maxTextWidth);
      const artistTotalHeight = artistLines.length * actualArtistSize * 1.2;

      // Position title above the artist text with spacing based on text length for abstract
      const titleLines = wrapText(ctx, titleText, maxTextWidth);

      // Calculate intelligent spacing based on text length with smooth scaling
      const combinedTextLength = titleText.length + artistText.length;

      // Create a smooth scaling factor based on text length (8-50 characters range)
      const minLength = 8;
      const maxLength = 50;
      const clampedLength = Math.max(minLength, Math.min(maxLength, combinedTextLength));
      const lengthRatio = (clampedLength - minLength) / (maxLength - minLength);

      // Scale spacing from 0.10x to 1.4x based on text length (very aggressive for short text)
      const spacingMultiplier = 0.10 + (lengthRatio * 1.30);
      const spacingBetweenTitleAndArtist = Math.max(actualTitleSize * spacingMultiplier, actualArtistSize * (spacingMultiplier * 0.8));

      // Render title with primary text color
      ctx.fillStyle = textColor.value;
      const titleY = height - bottomPadding - artistTotalHeight - spacingBetweenTitleAndArtist;
      renderMultiLineText(
        ctx, titleText, padding, titleY,
        maxTextWidth, actualTitleSize, true, 'left',
      );

      // Render artist with dedicated artist color for visual differentiation
      ctx.fillStyle = artistColor.value;
      const artistY = height - bottomPadding - (artistLines.length - 1) * (actualArtistSize * 1.2);
      renderMultiLineText(
        ctx, artistText, padding, artistY,
        maxTextWidth, actualArtistSize, true, 'left',
      );
    }
  }
  else if (selectedTemplate.value === 'text') {
    // Text-only template is centered

    // Maximized text sizes for thumbnail visibility
    const textOnlyTitleSize = Math.max(70 * scale, width * 0.18);
    const textOnlyArtistSize = Math.max(55 * scale, width * 0.12);

    const maxTextWidth = width - (padding * 2);
    const minTitleSize = Math.max(35 * scale, width * 0.07);
    const minArtistSize = Math.max(28 * scale, width * 0.06);

    // Calculate optimal sizes using helper function, ensuring title is always bigger
    let actualTitleSize = calculateOptimalTextSize(ctx, titleText, maxTextWidth, textOnlyTitleSize, minTitleSize, scale, true);
    const actualArtistSize = calculateOptimalTextSize(ctx, artistText, maxTextWidth, textOnlyArtistSize, minArtistSize, scale, true);

    // Ensure title is always at least 1.15x bigger than artist
    if (actualTitleSize <= actualArtistSize) {
      actualTitleSize = Math.max(actualArtistSize * 1.15, minTitleSize);
    }

    // Calculate spacing based on actual text length, not just multiline wrapping
    const titleLines = wrapText(ctx, titleText, maxTextWidth);
    const artistLines = wrapText(ctx, artistText, maxTextWidth);

    // Determine if text is "long" based on character count, not just line wrapping
    const combinedTextLength = titleText.length + artistText.length;
    const isLongText = combinedTextLength > 30; // Adjust threshold as needed

    const spacingBetweenTitleAndArtist = isLongText
      ? Math.max(actualTitleSize * 1.2, actualArtistSize * 0.8)  // Generous spacing for long text content
      : Math.max(actualTitleSize * 0.4, actualArtistSize * 0.3); // Moderate spacing for short text content

    // console.log('Text template - Combined length:', combinedTextLength, 'isLongText:', isLongText, 'spacing:', spacingBetweenTitleAndArtist);

    // Calculate total text block height for proper centering
    const titleTotalHeight = titleLines.length * actualTitleSize * 1.2;
    const artistTotalHeight = artistLines.length * actualArtistSize * 1.2;
    const totalTextHeight = titleTotalHeight + spacingBetweenTitleAndArtist + artistTotalHeight;

    // Position title (centered, starting from center minus half total height)
    ctx.fillStyle = primaryColor.value;
    const titleY = (height / 2) - (totalTextHeight / 2) + actualTitleSize;
    renderMultiLineText(
      ctx, titleText, width/2, titleY,
      maxTextWidth, actualTitleSize, true, 'center',
    );

    // Render artist (centered, positioned below title with intelligent spacing)
    ctx.fillStyle = textColor.value;
    const artistY = titleY + titleTotalHeight + spacingBetweenTitleAndArtist - actualArtistSize;
    renderMultiLineText(
      ctx, artistText, width/2, artistY,
      maxTextWidth, actualArtistSize, true, 'center',
    );
  }
  else if (selectedTemplate.value === 'artistic') {
    // Fill with background
    ctx.fillStyle = backgroundColor.value;
    ctx.fillRect(0, 0, width, height);

    // Create artistic patterns and elements - choose one pattern type

    if (patternType.value === 'geometric') {
      // Geometric diamond pattern
      const patternSize = Math.max(40 * scale, width * 0.08);
      ctx.fillStyle = primaryColor.value + '20'; // 12% opacity

      // Draw diamond/rhombus pattern
      for (let x = -patternSize; x < width + patternSize; x += patternSize) {
        for (let y = -patternSize; y < height + patternSize; y += patternSize) {
          ctx.save();
          ctx.translate(x + patternSize/2, y + patternSize/2);
          ctx.rotate(Math.PI / 4); // 45 degree rotation
          ctx.fillRect(-patternSize/4, -patternSize/4, patternSize/2, patternSize/2);
          ctx.restore();
        }
      }
    } else if (patternType.value === 'waves') {
      // Flowing curves and waves
      ctx.strokeStyle = accentColor.value + '40'; // 25% opacity
      ctx.lineWidth = Math.max(3 * scale, width * 0.006);

      // Create organic flowing lines
      const waveCount = 4;
      for (let i = 0; i < waveCount; i++) {
        ctx.beginPath();
        const startY = (height / waveCount) * i + (height / (waveCount * 2));
        const amplitude = Math.max(30 * scale, width * 0.06);
        const frequency = 0.01;

        ctx.moveTo(0, startY);
        for (let x = 0; x <= width; x += 5) {
          const y = startY + Math.sin(x * frequency + i) * amplitude;
          ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
    }

    // Removed inner gradient circle (light spot) for cleaner look

    // 4. Abstract artistic elements
    ctx.save();

    // Layered circles for depth in bottom-left
    const circleOpacities = ['30', '20', '10']; // Decreasing opacity
    const circleSizes = [0.15, 0.25, 0.35]; // Increasing size

    for (let i = 0; i < circleOpacities.length; i++) {
      ctx.fillStyle = accentColor.value + circleOpacities[i];
      ctx.beginPath();
      ctx.arc(width * 0.2, height * 0.8, width * circleSizes[i], 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();

    // Add text if needed with enhanced styling
    if (showTitle.value) {
      // Apply text shadow if enabled
      if (enableTextShadow.value) {
        ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
        ctx.shadowBlur = Math.max(8 * scale, width * 0.016);
        ctx.shadowOffsetX = Math.max(3 * scale, width * 0.006);
        ctx.shadowOffsetY = Math.max(3 * scale, width * 0.006);
      }

      // Reduced padding for tighter, more compact layout
      const artisticPadding = Math.max(40 * scale, width * 0.05);

      // Title with optimized sizing for thumbnail visibility
      const baseTitleSize = Math.max(55 * scale, width * 0.15);

      // Artist with significantly larger size for thumbnail readability
      const baseArtistSize = Math.max(48 * scale, width * 0.12);

      const maxTextWidth = width - (artisticPadding * 2);
      const minTitleSize = Math.max(35 * scale, width * 0.07);
      const minArtistSize = Math.max(30 * scale, width * 0.06);

      // Use helper function for optimal text sizing, ensuring title is always bigger
      let actualTitleSize = calculateOptimalTextSize(ctx, titleText, maxTextWidth, baseTitleSize, minTitleSize, scale, true);
      let actualArtistSize = calculateOptimalTextSize(ctx, artistText, maxTextWidth, baseArtistSize, minArtistSize, scale, true);

      // Ensure title is always at least 1.15x bigger than artist
      if (actualTitleSize <= actualArtistSize) {
        actualTitleSize = Math.max(actualArtistSize * 1.15, minTitleSize);
      }

      // Calculate spacing based on actual text length, not just multiline wrapping
      let titleLines = wrapText(ctx, titleText, maxTextWidth);
      let artistLines = wrapText(ctx, artistText, maxTextWidth);

      // Calculate intelligent spacing based on text length with smooth scaling
      const combinedTextLength = titleText.length + artistText.length;

      // Create a smooth scaling factor based on text length (8-50 characters range)
      const minLength = 8;
      const maxLength = 50;
      const clampedLength = Math.max(minLength, Math.min(maxLength, combinedTextLength));
      const lengthRatio = (clampedLength - minLength) / (maxLength - minLength);

      // Scale spacing from 0.10x to 1.6x based on text length (artistic has generous spacing)
      const spacingMultiplier = 0.10 + (lengthRatio * 1.50);
      let spacingBetweenTitleAndArtist = Math.max(actualTitleSize * spacingMultiplier, actualArtistSize * (spacingMultiplier * 0.8));

      // Check if text width would exceed canvas bounds and reduce font sizes if needed
      ctx.font = `bold ${actualTitleSize}px Arial`;
      const titleWidth = ctx.measureText(titleText).width;
      ctx.font = `bold ${actualArtistSize}px Arial`;
      const artistWidth = ctx.measureText(artistText).width;

      const maxAllowedWidth = width - (artisticPadding * 2);
      const titleOverflow = titleWidth > maxAllowedWidth;
      const artistOverflow = artistWidth > maxAllowedWidth;

      if (titleOverflow || artistOverflow) {
        // Calculate reduction factor needed based on the worst overflow, but not too aggressive
        const titleReduction = titleOverflow ? Math.max(maxAllowedWidth / titleWidth * 0.98, 0.8) : 1; // Max 20% reduction
        const artistReduction = artistOverflow ? Math.max(maxAllowedWidth / artistWidth * 0.98, 0.8) : 1; // Max 20% reduction
        const reductionFactor = Math.min(titleReduction, artistReduction);

        // Reduce font sizes but maintain readability
        const newTitleSize = Math.max(actualTitleSize * reductionFactor, Math.max(minTitleSize, actualTitleSize * 0.75));
        const newArtistSize = Math.max(actualArtistSize * reductionFactor, Math.max(minArtistSize, actualArtistSize * 0.75));

        actualTitleSize = newTitleSize;
        actualArtistSize = newArtistSize;

        // Ensure title is still bigger than artist after reduction
        if (actualTitleSize <= actualArtistSize) {
          actualTitleSize = Math.max(actualArtistSize * 1.15, minTitleSize);
        }

        // Recalculate with new font sizes
        titleLines = wrapText(ctx, titleText, maxTextWidth);
        artistLines = wrapText(ctx, artistText, maxTextWidth);
        // Recalculate spacing with new font sizes using the same intelligent scaling
        const newSpacingMultiplier = 0.10 + (lengthRatio * 1.50);
        spacingBetweenTitleAndArtist = Math.max(actualTitleSize * newSpacingMultiplier, actualArtistSize * (newSpacingMultiplier * 0.8));
      }

      // Calculate text positions and render with multi-line support
      let artistY;

      if (textPosition.value === 'center') {
        // Center alignment with intelligent spacing
        const titleTotalHeight = titleLines.length * actualTitleSize * 1.2;
        const artistTotalHeight = artistLines.length * actualArtistSize * 1.2;
        const totalTextHeight = titleTotalHeight + spacingBetweenTitleAndArtist + artistTotalHeight;

        // Render title with primary text color
        ctx.fillStyle = textColor.value;
        const titleY = (height / 2) - (totalTextHeight / 2) + actualTitleSize;
        renderMultiLineText(
          ctx, titleText, width / 2, titleY,
          maxTextWidth, actualTitleSize, true, 'center',
        );

        // Render artist with dedicated artist color for visual differentiation
        ctx.fillStyle = artistColor.value;
        artistY = titleY + titleTotalHeight + spacingBetweenTitleAndArtist - actualArtistSize;
        renderMultiLineText(
          ctx, artistText, width / 2, artistY,
          maxTextWidth, actualArtistSize, true, 'center',
        );
      } else if (textPosition.value === 'bottom-right') {
        // Right alignment with intelligent spacing
        const artistTotalHeight = artistLines.length * actualArtistSize * 1.2;

        // Render title with primary text color
        ctx.fillStyle = textColor.value;
        const titleY = height - artisticPadding - artistTotalHeight - spacingBetweenTitleAndArtist;
        renderMultiLineText(
          ctx, titleText, width - artisticPadding, titleY,
          maxTextWidth, actualTitleSize, true, 'right',
        );

        // Render artist with dedicated artist color for visual differentiation
        ctx.fillStyle = artistColor.value;
        artistY = height - artisticPadding - (artistLines.length - 1) * (actualArtistSize * 1.2);
        renderMultiLineText(
          ctx, artistText, width - artisticPadding, artistY,
          maxTextWidth, actualArtistSize, true, 'right',
        );
      } else { // bottom-left (default)
        // Left alignment with same logic as other templates (minimal, gradient, abstract)
        const artistTotalHeight = artistLines.length * actualArtistSize * 1.2;

        // Render title with primary text color
        ctx.fillStyle = textColor.value;
        const titleY = height - artisticPadding - artistTotalHeight - spacingBetweenTitleAndArtist;
        renderMultiLineText(
          ctx, titleText, artisticPadding, titleY,
          maxTextWidth, actualTitleSize, true, 'left',
        );

        // Render artist with dedicated artist color for visual differentiation
        ctx.fillStyle = artistColor.value;
        artistY = height - artisticPadding - (artistLines.length - 1) * (actualArtistSize * 1.2);
        renderMultiLineText(
          ctx, artistText, artisticPadding, artistY,
          maxTextWidth, actualArtistSize, true, 'left',
        );
      }

      // TODO: Add text outline support for multi-line text if shadow is enabled
      // if (enableTextShadow.value) {
      //   // Text shadow for multi-line text needs special implementation
      // }

      // Reset context
      ctx.textAlign = 'left';
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      ctx.lineWidth = 1;
    }
  }

  // Draw logo after all template elements
  // Determine which logo to use based on background color
  const useLightLogo = isDarkColor(backgroundColor.value);
  const logo = useLightLogo ? lightLogo : darkLogo;

  // Load and draw the logo
  const logoImg = new Image();
  logoImg.src = logo;
  await new Promise((resolve) => {
    logoImg.onload = resolve;
  });

  // Calculate logo size (approximately 10% of the width)
  const logoSize = width * 0.1;

  // Adjust logo padding based on template type
  let logoPadding;
  if (selectedTemplate.value === 'vintage') {
    // Use enhanced padding for vintage template to account for thicker border
    logoPadding = Math.max(70 * scale, width * 0.09);
  } else if (selectedTemplate.value === 'artistic') {
    // Use same reduced padding as text for artistic template
    logoPadding = Math.max(40 * scale, width * 0.05);
  } else {
    logoPadding = padding * 0.5;
  }

  // Draw logo in top-right corner with appropriate padding
  ctx.drawImage(
    logoImg,
    width - logoSize - logoPadding,
    logoPadding,
    logoSize,
    logoSize,
  );

  return ctx.canvas;
};

const generateArtwork = async () => {
  if (isCurrentTemplatePremium.value && !props.isPremium) {
    router.push('/upgrade?from=v3_cover');
    return;
  }

  // Set loading state
  isGenerating.value = true;

  try {
    // Scroll to top to show loading state and generated result
    // Use multiple approaches for better cross-browser compatibility
    document.documentElement.scrollTop = 300;
    document.body.scrollTop = 300;
    window.scrollTo({ top: 300, behavior: 'smooth' });

    // Small delay to ensure scroll completes
    await new Promise(resolve => setTimeout(resolve, 100));
    // Create output canvas with exact 1500x1500 dimensions
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = 1500;
    outputCanvas.height = 1500;
    const ctx = outputCanvas.getContext('2d');

    // Use the same rendering function that renders the preview
    // but with scale=3 to get high-quality output
    await renderTemplate(ctx, 1500, 1500, 3);

    // Convert to data URL with maximum quality
    const artworkDataUrl = outputCanvas.toDataURL('image/jpeg', 1.0);

    // Convert data URL to a Blob and then to a File object
    const response = await fetch(artworkDataUrl);
    const blob = await response.blob();
    const filename = `${props.title || 'Release'}-cover.jpg`;
    const file = new File([blob], filename, { type: 'image/jpeg' });

    // Emit artwork data to parent component
    emit('update:artwork', {
      dataUrl: artworkDataUrl,
      file: file,
      template: selectedTemplate.value,
      colors: {
        primary: primaryColor.value,
        accent: accentColor.value,
        background: backgroundColor.value,
        text: textColor.value,
      },
    });
  } catch (error) {
    console.error('Error generating artwork:', error);
  } finally {
    // Reset loading state
    isGenerating.value = false;
  }
};

// Watch for props changes
watch(() => props.initialTemplate, (newTemplate) => {
  if (newTemplate && newTemplate !== selectedTemplate.value) {
    setTemplate(newTemplate);
  }
});

// Initialize
onMounted(() => {
  // Give Vue time to render the canvas element
  nextTick(() => {
    // Initialize template with default settings
    setTemplate(selectedTemplate.value);
  });
});
</script>

<style lang="scss" scoped>
.cover-template-editor {
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @media (min-width: 768px) {
    flex-direction: row;
  }

  .template-preview-container {
    flex: 1;

    .template-preview {
      width: 100%;
      aspect-ratio: 1/1;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
      display: flex;
      justify-content: center;
      align-items: center;

      .preview-canvas {
        width: 100%;
        height: 100%;
        object-fit: contain;
        border-radius: 12px;
      }
    }
  }

  .template-controls {
    width: 100%;
    max-width: 400px;

    .control-section {
      margin-bottom: 1.5rem;

      .control-label {
        display: block;
        font-weight: 600;
        margin-bottom: 0.5rem;
        color: var(--text-strong, #363636);
      }

      .template-selector {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-bottom: 1rem;

        .template-button {
          position: relative;
          background-color: var(--scheme-main-bis, #f5f5f5);
          border: 1px solid var(--border-color, #dbdbdb);
          border-radius: 4px;
          padding: 0.5rem 1rem;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 120px;
          display: flex;
          align-items: center;
          justify-content: center;

          .template-name {
            margin-right: 0.5rem;
          }

          .premium-icon {
            font-size: 0.75rem;
            color: #ed8936;
            position: absolute;
            top: 4px;
            right: 4px;
            transition: color 0.2s ease;
          }

          &:hover {
            background-color: var(--scheme-main-ter, #eeeeee);
            transform: translateY(-1px);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

            .premium-icon {
              color: #dd6b20;
            }
          }

          &.active {
            background-color: var(--primary-color, #3e8ed0);
            color: white;
            border-color: var(--primary-color, #3e8ed0);

            .premium-icon {
              color: orange;
            }
          }

          &.premium {
            background-color: var(--scheme-main-bis, #f5f5f5);
            border: 1px solid var(--border-color, #dbdbdb);
            color: var(--text-light, #7a7a7a);

            &:hover {
              background-color: var(--scheme-main-ter, #eeeeee);
              color: var(--text-strong, #363636);
              border-color: #ed8936;
            }

            // When both premium and active classes are present, active takes precedence
            &.active {
              background-color: var(--primary-color, #3e8ed0) !important;
              color: white !important;
              border-color: var(--primary-color, #3e8ed0) !important;

              .premium-icon {
                color: orange !important;
              }

              &:hover {
                background-color: var(--primary-color-dark, #2c5aa0) !important;
                color: white !important;
                border-color: var(--primary-color-dark, #2c5aa0) !important;
              }
            }
          }
        }
      }

      .custom-colors-toggle {
        margin-top: 1.5rem;

        .control-subtitle-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          padding: 0.5rem 0;
          transition: all 0.2s ease;

          &:hover {
            opacity: 0.8;
          }

          .toggle-icon {
            color: var(--text-light, #7a7a7a);
            font-size: 0.8rem;
          }
        }
      }

      .control-subtitle {
        font-size: 1rem;
        font-weight: 600;
        margin: 0;
        color: var(--text-strong, #363636);
        padding-bottom: 0.5rem;
        border-bottom: 1px solid var(--border-color, #dbdbdb);
      }

      .palette-selector {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
        gap: 0.75rem;
        margin-bottom: 1.5rem;

        .palette-option {
          background-color: var(--scheme-main, #ffffff);
          border-radius: 6px;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 2px solid transparent;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);

          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          }

          &.active {
            border-color: var(--primary-color, #3e8ed0);
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          }

          .palette-preview {
            display: flex;
            height: 40px;

            .palette-color {
              flex: 1;

              &.primary-color {
                flex: 2;
              }

              &.accent-color {
                flex: 1;
              }

              &.background-color {
                flex: 1;
                border-left: 1px solid rgba(0,0,0,0.05);
              }
            }
          }

          .palette-name {
            display: block;
            text-align: center;
            font-size: 0.75rem;
            padding: 0.4rem;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            font-weight: 500;
            background-color: var(--scheme-main-bis, #f5f5f5);
          }
        }
      }

      .manual-color-controls {
        margin-top: 1rem;
      }

      .color-picker-row {
        display: flex;
        align-items: center;

        .color-picker {
          height: 40px;
          width: 80px;
          padding: 0;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            transform: scale(1.05);
          }

          &::-webkit-color-swatch-wrapper {
            padding: 0;
          }

          &::-webkit-color-swatch {
            border: none;
            border-radius: 4px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }
        }

        .color-value {
          margin-left: 1rem;
          font-family: monospace;
          font-size: 0.9rem;
          color: var(--text-light, #7a7a7a);
        }
      }

      .toggle-control {
        margin-top: 0.5rem;
      }
    }

    .control-actions {
      margin-top: 2rem;

      .btn-generate-artwork {
        width: 100%;
        background: linear-gradient(135deg, var(--primary-color, #3e8ed0) 0%, var(--info-color, #3e8ed0) 100%);
        border: none;
        color: white;
        font-weight: 600;
        padding: 0.75rem 1.5rem;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.3s ease;
        position: relative;

        .button-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .premium-icon {
          font-size: 0.875rem;
          color: orange;
        }

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(62, 142, 208, 0.3);
        }

        &.premium-locked {
          background: linear-gradient(135deg, #ed8936 0%, #dd6b20 100%);
          cursor: pointer;

          &:hover {
            box-shadow: 0 8px 16px rgba(237, 137, 54, 0.3);
          }
        }
      }
    }
  }
}

// Dark mode styling
:global(.body--dark) {
  .cover-template-editor {
    .template-controls {
      .control-section {
        .template-selector {
          .template-button {
            background-color: var(--scheme-main-bis, #2d3748);
            border-color: var(--border-color, #4a5568);
            color: white;

            &:hover {
              background-color: var(--scheme-main-ter, #4a5568);
            }

            &.active {
              background-color: var(--primary-color, #3e8ed0);
              border-color: var(--primary-color, #3e8ed0);
            }

            &.premium {
              background-color: var(--scheme-main-bis, #2d3748);
              border-color: var(--border-color, #4a5568);
              color: var(--text-light, #a0aec0);

              &.active {
                background-color: var(--primary-color, #3e8ed0) !important;
                border-color: var(--primary-color, #3e8ed0) !important;
                color: white !important;

                .premium-icon {
                  color: orange !important;
                }
              }
            }
          }
        }

        .palette-selector {
          .palette-option {
            background-color: var(--scheme-main-bis, #2d3748);
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);

            &:hover {
              box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
            }

            &.active {
              border-color: var(--primary-color, #3e8ed0);
              box-shadow: 0 2px 8px rgba(62, 142, 208, 0.3);
            }

            .palette-name {
              background-color: var(--scheme-main, #1a202c);
              color: var(--text-light, #e2e8f0);
            }
          }
        }

        .custom-colors-toggle {
          .control-subtitle-wrapper {
            .toggle-icon {
              color: var(--text-light, #a0aec0);
            }
          }
        }

        .control-subtitle {
          color: var(--text-strong, #e2e8f0);
          border-bottom-color: var(--border-color, #4a5568);
        }

        .color-value {
          color: var(--text-light, #a0aec0);
        }
      }
    }
  }
}

.mt-3 {
  margin-top: 1rem;
}

// Artistic template controls styling
.artistic-controls {
  .position-selector {
    display: flex;
    gap: 0.5rem;

    .position-button {
      flex: 1;
      padding: 0.5rem 1rem;
      border: 2px solid var(--border-color, #e2e8f0);
      border-radius: 6px;
      background-color: white;
      color: var(--text-color, #2d3748);
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        border-color: var(--primary-color, #3e8ed0);
        background-color: var(--primary-color-light, #e6f3ff);
      }

      &.active {
        border-color: var(--primary-color, #3e8ed0);
        background-color: var(--primary-color, #3e8ed0);
        color: white;
      }
    }
  }

  .toggle-control {
    margin-top: 1rem;
  }
}

// Dark mode for artistic controls
:global(.body--dark) {
  .cover-template-editor {
    .artistic-controls {
      .position-selector {
        .position-button {
          background-color: var(--scheme-main-bis, #2d3748);
          border-color: var(--border-color, #4a5568);
          color: white;

          &:hover {
            background-color: var(--scheme-main-ter, #4a5568);
            border-color: var(--primary-color, #3e8ed0);
          }

          &.active {
            background-color: var(--primary-color, #3e8ed0);
            border-color: var(--primary-color, #3e8ed0);
          }
        }
      }
    }
  }
}
</style>